import { revalidatePath } from "@/lib/revalidate";

import { ApiError, createDefinition, publishVersion } from "@/lib/api/client";
import type { SportDefinitionDoc } from "@/lib/api/types";

export type SaveResult = { ok: true; version: number } | { ok: false; error: string };
export type PublishResult =
  | { ok: true }
  | { ok: false; error: string; violations: string[] };

/** Autosave. A sport has one open draft that this overwrites — see the BFF's
 * save_draft. Failures are surfaced but not fatal: a draft mid-edit is often
 * incomplete, and publish is where it must be valid. */
export async function saveDraftAction(doc: SportDefinitionDoc): Promise<SaveResult> {
  try {
    const result = await createDefinition(doc);
    return { ok: true, version: result.version };
  } catch (error) {
    const message = error instanceof ApiError ? error.message : "could not save the draft";
    return { ok: false, error: message };
  }
}

/** Save then publish, so the published version is exactly what's on screen. */
export async function publishSportAction(doc: SportDefinitionDoc): Promise<PublishResult> {
  try {
    const saved = await createDefinition(doc);
    await publishVersion(saved.sportCode, saved.version);
    revalidatePath("/sports");
    revalidatePath("/dashboard");
    return { ok: true };
  } catch (error) {
    if (error instanceof ApiError) {
      return { ok: false, error: error.message, violations: error.violations };
    }
    return { ok: false, error: "Unexpected error publishing the sport.", violations: [] };
  }
}
